import { useEffect, useRef, useState } from 'react';
import { formatMoney } from '@/lib/format';

interface ProfitRingProps {
  profit: number;
  revenue: number;
  cost: number;
  margin: number;
  currencySymbol: string;
  size?: number;
}

/**
 * Indicador circular de ganancias con animación suave.
 * El arco representa el margen (ganancia / ingreso). Sin SVG: usa conic-gradient
 * sobre tokens del sistema de diseño.
 */
export default function ProfitRing({
  profit,
  revenue,
  cost,
  margin,
  currencySymbol,
  size = 240,
}: ProfitRingProps) {
  const target = revenue > 0 ? Math.max(0, Math.min(100, (profit / revenue) * 100)) : 0;
  const [pct, setPct] = useState(0);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const duration = 750;
    const startValue = 0;
    const startTime = performance.now();
    const animate = (now: number) => {
      const t = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setPct(startValue + (target - startValue) * eased);
      if (t < 1) frameRef.current = requestAnimationFrame(animate);
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [target]);

  const isLoss = profit < 0;
  const arcColor = isLoss ? 'oklch(var(--accent-500))' : 'oklch(var(--primary-500))';

  return (
    <div className="flex flex-col items-center">
      <div
        className="relative rounded-full transition-transform"
        style={{
          width: size,
          height: size,
          backgroundImage: `conic-gradient(${arcColor} ${pct}%, oklch(var(--background-200)) ${pct}% 100%)`,
        }}
        role="img"
        aria-label={`Margen de ganancia ${margin.toFixed(1)} por ciento`}
      >
        <div
          className="absolute rounded-full bg-background-50 flex flex-col items-center justify-center text-center px-4"
          style={{ inset: Math.round(size * 0.1) }}
        >
          <span className="text-xs font-medium uppercase tracking-wide text-foreground-500">
            {isLoss ? 'Pérdida' : 'Ganancia'}
          </span>
          <span
            className={`mt-1 font-heading text-xl md:text-2xl font-bold whitespace-nowrap ${
              isLoss ? 'text-accent-700' : 'text-foreground-950'
            }`}
          >
            {formatMoney(profit, currencySymbol)}
          </span>
          <span className="mt-1 text-xs text-foreground-500">
            Margen {margin.toFixed(1)}%
          </span>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-4 text-xs">
        <span className="inline-flex items-center gap-1.5 text-foreground-600">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: arcColor }} />
          Ganancia {formatMoney(profit, currencySymbol)}
        </span>
        <span className="inline-flex items-center gap-1.5 text-foreground-600">
          <span className="w-2.5 h-2.5 rounded-full bg-background-300" />
          Costo {formatMoney(cost, currencySymbol)}
        </span>
      </div>
    </div>
  );
}